import { useState } from 'react';
import { VehicleMedia as Vehicle360Media } from '../../../components/VehicleMedia/VehicleMedia';
import { useWizard } from '../wizardContext';
import { fmtBRL } from '../utils/finance';
import { PinIcon } from './icons';

const Rows = () => {
  const { state, financedAmount } = useWizard();
  if (state.step < 4) return null;
  const s = state.simulation;
  return (
    <>
      <div className="lpfs-vp-divider" />
      <div className="lpfs-vp-rows">
        <div className="lpfs-vp-row">
          <span>Entrada</span>
          <strong>{fmtBRL(s.downPayment)}</strong>
        </div>
        {state.hasTradeIn && s.tradeInValue > 0 && (
          <div className="lpfs-vp-row">
            <span>Seminovo</span>
            <strong>{fmtBRL(s.tradeInValue)}</strong>
          </div>
        )}
        <div className="lpfs-vp-row is-strong">
          <span>A financiar</span>
          <strong>{fmtBRL(financedAmount)}</strong>
        </div>
      </div>
    </>
  );
};

const UnitBlock = () => {
  const { unit } = useWizard();
  if (!unit) return null;
  return (
    <div className="lpfs-vp-unit">
      <span className="lpfs-vp-unit-pin">
        <PinIcon />
      </span>
      <span className="lpfs-vp-unit-info">
        <span className="lpfs-vp-unit-label">Unidade selecionada</span>
        <span className="lpfs-vp-unit-name">{unit.name}</span>
        {unit.address && <span className="lpfs-vp-unit-row">{unit.address}</span>}
        {unit.phone && <span className="lpfs-vp-unit-row">{unit.phone}</span>}
        {unit.openingHours && <span className="lpfs-vp-unit-row">{unit.openingHours}</span>}
        {unit.mapLink && (
          <a className="lpfs-vp-unit-map" href={unit.mapLink} target="_blank" rel="noopener noreferrer">
            Ver no mapa
          </a>
        )}
      </span>
    </div>
  );
};

/** Mídia do veículo (padrão do bloco 39): mostra a CAPA (foto da versão ou do
    modelo); se houver 360 (galeria da versão ou do modelo), o arraste revela o
    giro e aparece a dica "Arraste para girar". Sem 360, fica só a capa — sem dica. */
const VehicleMedia = () => {
  const { model, version } = useWizard();
  if (!model) return null;
  const cover = version?.image || model.image;
  const frames360 =
    version?.frames360 && version.frames360.length > 1
      ? version.frames360
      : model.frames360 && model.frames360.length > 1
        ? model.frames360
        : [];
  if (!cover && !frames360.length) return null;
  const has360 = frames360.length > 1;
  return (
    <div className="lpfs-vp-img is-360">
      <Vehicle360Media
        frames={frames360}
        cover={cover || undefined}
        alt={`${model.name}${version ? ` ${version.name}` : ''}`}
        maxHeight={{ mobile: '14rem', desktop: '16rem' }}
        resetSignal={`${model.id}-${version?.id ?? ''}`}
        hint={{ show: has360, label: 'Arraste para girar', color: 'var(--lpfs-text-body)' }}
      />
    </div>
  );
};

export const VehiclePanel = () => {
  const { model, version, minVersionPrice, state } = useWizard();
  if (!model) {
    return (
      <aside className="lpfs-vehicle-panel" aria-label="Veículo selecionado">
        <div className="lpfs-vp-img">
          <span className="lpfs-vp-empty">Selecione um modelo para visualizar</span>
        </div>
      </aside>
    );
  }
  const price = state.vehicle.price || minVersionPrice(model);
  const versionLine = version ? version.name : 'Escolha a versão';
  return (
    <aside className="lpfs-vehicle-panel" aria-label="Veículo selecionado">
      <VehicleMedia />
      <h3 className="lpfs-vp-name">{model.name}</h3>
      <p className="lpfs-vp-version">{versionLine}</p>
      <p className="lpfs-vp-price-label">Valor do modelo</p>
      <p className="lpfs-vp-price">{fmtBRL(price)}</p>
      <Rows />
      <UnitBlock />
    </aside>
  );
};

/** Barra sticky (mobile) na parte inferior da área do bloco, acompanhando o
    scroll — link "Ver mais" expande os detalhes (referência Geely). */
export const MobileVehicle = () => {
  const { model, version, minVersionPrice, state, renderImage } = useWizard();
  const [open, setOpen] = useState(false);
  if (!model) return null;
  const price = state.vehicle.price || minVersionPrice(model);
  const thumb = version?.image || model.image;
  return (
    <div className={`lpfs-mobile-vehicle${open ? ' is-open' : ''}`}>
      <div className="lpfs-mv-bar">
        {thumb && (
          <span className="lpfs-mv-thumb">
            {renderImage({ src: thumb, alt: '', width: 120, height: 80, loading: 'lazy' })}
          </span>
        )}
        <span className="lpfs-mv-info">
          <span className="lpfs-mv-name">
            {model.name}
            {version ? ` ${version.name}` : ''}
          </span>
          <br />
          <span className="lpfs-mv-price">{fmtBRL(price)}</span>
        </span>
        <button type="button" className="lpfs-mv-more" aria-expanded={open} onClick={() => setOpen((p) => !p)}>
          {open ? 'Ver menos' : 'Ver mais'}
        </button>
      </div>
      {/* Abre ABAIXO da barra, dentro do mesmo box flutuante (a barra é sticky
          bottom, então o box cresce para cima mantendo os detalhes sob ela).
          Sem repetir o valor: valores da simulação + unidade. */}
      {open && (
        <div className="lpfs-mv-detail">
          <Rows />
          <UnitBlock />
        </div>
      )}
    </div>
  );
};
